import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { timeAgo } from "../lib/community";
import {
  addComment,
  deleteComment,
  deleteCommunityPost,
  setPostLiked,
  setPostPinned,
  watchComments,
  watchCommunityPost,
  watchMembers,
  watchMyLikes,
  type CommunityComment,
  type CommunityMember,
  type CommunityPost,
} from "../lib/firebase";
import { MemberAvatar } from "../components/CommunityGate";
import { PostCard } from "./CommunityPage";

export function CommunityPostPage() {
  const { postId = "" } = useParams();
  const { user, isAdmin, displayName } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState<CommunityPost | null | undefined>(undefined);
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [members, setMembers] = useState<Map<string, CommunityMember>>(new Map());
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchCommunityPost(postId, setPost), [postId]);
  useEffect(() => watchComments(postId, setComments), [postId]);
  useEffect(() => watchMembers((ms) => setMembers(new Map(ms.map((m) => [m.uid, m])))), []);
  useEffect(() => (user ? watchMyLikes(user.uid, setLiked) : undefined), [user]);

  if (!user) return null;
  if (post === undefined) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;
  if (post === null) {
    return (
      <main className="container-tight">
        <div className="empty-state"><p>โพสต์นี้ถูกลบไปแล้ว</p><Link to="/chat">กลับ Chat โลก</Link></div>
      </main>
    );
  }

  const canDelete = isAdmin || post.authorUid === user.uid;

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return setError("พิมพ์ความเห็นก่อนนะ");
    setBusy(true);
    setError(null);
    try {
      await addComment(postId, { uid: user!.uid, displayName }, text.trim());
      setText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="container-tight">
      <div className="page-header">
        <Link to="/chat" className="page-header__back">← Chat โลก</Link>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {isAdmin && (
            <button className="btn-secondary" onClick={() => setPostPinned(post.id, !post.pinned)}>
              {post.pinned ? "เลิกปักหมุด" : "ปักหมุด"}
            </button>
          )}
          {canDelete && (
            <button
              className="btn-ghost"
              onClick={async () => {
                if (!confirm("ลบโพสต์นี้?")) return;
                await deleteCommunityPost(post.id);
                navigate("/chat");
              }}
            >
              ลบโพสต์
            </button>
          )}
        </div>
      </div>

      <PostCard
        post={post}
        full
        author={members.get(post.authorUid)}
        liked={liked.has(post.id)}
        onLike={(on) => setPostLiked(post.id, user.uid, on)}
      />

      <section className="cm-comments">
        {comments.map((c) => (
          <div key={c.id} className="cm-comment">
            <MemberAvatar name={c.authorName} points={members.get(c.authorUid)?.points} avatar={members.get(c.authorUid)?.avatar} />
            <div className="cm-comment__bubble">
              <strong>{c.authorName}</strong> <span className="cm-post__meta">{timeAgo(c.createdAt)}</span>
              <p>{c.text}</p>
              {(isAdmin || c.authorUid === user.uid) && (
                <button className="link-btn cm-comment__delete" onClick={() => deleteComment(postId, c.id)}>ลบ</button>
              )}
            </div>
          </div>
        ))}

        <form className="cm-comment-form" onSubmit={send}>
          <textarea
            placeholder="เขียนความเห็น…"
            value={text}
            maxLength={5000}
            onChange={(e) => { setText(e.target.value); setError(null); }}
          />
          {error && <p className="error-inline">{error}</p>}
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? "กำลังส่ง…" : "ส่งความเห็น"}</button>
        </form>
      </section>
    </main>
  );
}
